import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Bell, X } from "lucide-react";
import { useNotifications } from "../context/NotifContext";
import { useT } from "../context/LanguageContext";
import { useState } from "react";

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return d.toLocaleDateString();
}

const TYPE_COLOR = {
  dm: "text-[#7C3AED]",
  pair_chat: "text-[#7C3AED]",
  global_feed: "text-[#EA580C]",
  match_reminder: "text-[#34C759]",
  player_banned: "text-[#E11D48]",
};

export default function NotificationBell() {
  const { items, unread, markAllRead, markOneRead } = useNotifications();
  const { t } = useT();
  const [open, setOpen] = useState(false);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Lock body scroll while modal is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="notification-bell"
        className="relative border border-[#E4E4E7] dark:border-[#2A2A30] text-[#09090B] dark:text-[#F4F4F5] px-3 py-2 hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors flex items-center"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#E11D48] text-white font-mono text-[10px] font-bold flex items-center justify-center rounded-full border border-black"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-modal"
          className="fixed inset-0 z-[1000] flex items-start justify-center px-4 pt-20 sm:pt-24 pb-6 bg-black/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={t("notif.title")}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div
            data-testid="notification-panel"
            className="w-full max-w-md bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] shadow-[0_20px_60px_rgba(0,0,0,0.6)] max-h-[calc(100vh-7rem)] flex flex-col"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4E4E7] dark:border-[#2A2A30]">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">{t("notif.title")}</div>
              <div className="flex items-center gap-3">
                {unread > 0 && (
                  <button
                    onClick={markAllRead}
                    data-testid="notification-mark-all"
                    className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] hover:text-[#7C3AED] transition-colors"
                  >
                    {t("notif.markAllRead")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  data-testid="notification-close"
                  aria-label="Close"
                  className="text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {items.length === 0 ? (
                <div className="p-6 text-center font-mono text-[11px] text-[#A1A1AA] tracking-[0.2em]" data-testid="notification-empty">
                  {t("notif.empty")}
                </div>
              ) : (
                <ul className="divide-y divide-[#E4E4E7] dark:divide-[#2A2A30]">
                  {items.map((n) => {
                    const color = TYPE_COLOR[n.type] || "text-[#09090B] dark:text-[#F4F4F5]";
                    return (
                      <li key={n.id} data-testid={`notif-${n.id}`}>
                        <Link
                          to={n.link || "/dashboard"}
                          onClick={async () => { setOpen(false); if (!n.read) await markOneRead(n.id); }}
                          className={`block px-4 py-3 hover:bg-[#F4F4F5] dark:hover:bg-[#0F0F14] transition-colors ${!n.read ? "bg-[#7C3AED]/5" : ""}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className={`font-heading text-sm uppercase tracking-wider truncate ${color}`}>{n.title}</div>
                            {!n.read && <span className="mt-1.5 w-2 h-2 bg-[#7C3AED] rounded-full flex-shrink-0" />}
                          </div>
                          {n.body && <div className="font-body text-xs text-[#52525B] dark:text-[#A1A1AA] mt-1 line-clamp-2">{n.body}</div>}
                          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A1A1AA] mt-1.5">{formatWhen(n.created_at, t)}</div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
